import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import "core-js/modules/es.array.find.js";
import "core-js/modules/es.object.to-string.js";
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import config from 'utils/config';
import MindsetManageCalendarModal from 'components/MindsetManageCalendarModal';
import { getAgentElement } from './mindsetAI';
import { formatCalendarData } from './useFetchFormattedCalendarData';
import { updateExtraSituationalAwarenessContext } from './useExtraSituationalAwarenessContext';
import { sendMessageWhenIdle } from './sendMessageWhenIdle';
import userCalendarKeys from 'components/UserCalendarIntegration/hooks/queryKeys';
import { jsx as _jsx } from "react/jsx-runtime";
export var defaultHelixMindsetManageCalendarToolDescription = 'UI ACTION: Open a modal that lets the user connect or manage their calendar (Google or Microsoft) for automatic meeting joining by Helix and for enabling and managing meeting autojoin options for Helix.';
var description = config.helixMindsetManageCalendarToolDescription || defaultHelixMindsetManageCalendarToolDescription;
var CALENDAR_PROVIDERS = ['google', 'microsoft'];
function getRequestedProvider(args) {
  return CALENDAR_PROVIDERS.find(function (provider) {
    return provider === args.provider;
  });
}
export function useMindsetManageCalendarModalTool(mindsetId) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    defaultProvider = _useState4[0],
    setDefaultProvider = _useState4[1];
  var queryClient = useQueryClient();
  var tool = useMemo(function () {
    return {
      name: 'open_manage_calendar_modal',
      description: description,
      runningDescription: 'Opening manage calendar modal',
      completedDescription: 'Opened manage calendar modal',
      parameters: {
        type: 'object',
        properties: {
          provider: {
            type: 'string',
            enum: CALENDAR_PROVIDERS,
            description: "The calendar provider to preselect, if the user already said which one they want (e.g. 'google' if they asked to connect their Google calendar)."
          }
        }
      },
      handler: function () {
        var _handler = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(args) {
          return _regeneratorRuntime.wrap(function (_context) {
            while (1) switch (_context.prev = _context.next) {
              case 0:
                setDefaultProvider(getRequestedProvider(args));
                setIsOpen(true);
                return _context.abrupt("return", {
                  success: true
                });
              case 1:
              case "end":
                return _context.stop();
            }
          }, _callee);
        }));
        function handler(_x) {
          return _handler.apply(this, arguments);
        }
        return handler;
      }()
    };
  }, []);
  var onClose = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var freshCalendarInfo, calendarSettings, isCalendarLinked, isAutoJoinEnabled;
    return _regeneratorRuntime.wrap(function (_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          _context2.next = 1;
          return queryClient.invalidateQueries({
            queryKey: userCalendarKeys.userCalendar
          });
        case 1:
          // invalidateQueries resolves once the refetch completes, but doesn't
          // update any hook's return value synchronously - read the now-fresh
          // cache directly rather than risk acting on stale calendar settings.
          freshCalendarInfo = queryClient.getQueryData(userCalendarKeys.userCalendar);
          calendarSettings = formatCalendarData(freshCalendarInfo);
          isCalendarLinked = calendarSettings.hasCalendarLinked, isAutoJoinEnabled = calendarSettings.helixCanAutoJoinMeetings;
          updateExtraSituationalAwarenessContext({
            calendarSettings: JSON.stringify(calendarSettings)
          });
          setIsOpen(false);
          if (!isCalendarLinked) {
            sendMessageWhenIdle(getAgentElement(mindsetId), 'The user has not linked their calendar yet. Ask the user if they would like to link their calendar and provide guidance on how to do so. Open the calendar management modal when needed.');
          } else {
            if (isAutoJoinEnabled) {
              sendMessageWhenIdle(getAgentElement(mindsetId), 'The user has successfully linked their calendar and enabled autojoining meetings for Helix. Acknowledge this and provide any next steps or confirmations as needed.');
            }
            if (!isAutoJoinEnabled) {
              sendMessageWhenIdle(getAgentElement(mindsetId), 'The user has linked their calendar but has not enabled autojoining meetings for Helix. Ask the user if they would like to enable the autojoining of meetings for Helix and provide guidance on how to do so. Only open the manage calendar modal if the user requests it or confirms they want to enable autojoining meetings for Helix.');
            }
          }
        case 2:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  })), [mindsetId, queryClient]);
  var onCalendarLinked = useCallback(function () {
    updateExtraSituationalAwarenessContext({
      calendarLinked: 'true'
    });
  }, []);
  var modal = isOpen ? /*#__PURE__*/_jsx(MindsetManageCalendarModal, {
    onClose: onClose,
    onCalendarLinked: onCalendarLinked,
    defaultProvider: defaultProvider
  }) : null;
  return {
    tool: tool,
    modal: modal
  };
}